import type { SluggedId } from "Models";
import type { AdKitKey } from "types/enums";
import type { ClubhouseAdKitDto } from "entities/responses/adkit";
import type {
  CreateAdKitDto,
  CreateAdKitLocationDto,
} from "entities/requests/adkit";
import type { PlacementCatalogEntry } from "../../placementCatalog";

/**
 * Everything the operator sets up, held in the browser until Save draft or
 * Go live (CLUB-2644: nothing is written to the API before step 4).
 */

export type SetupStep = 1 | 2 | 3 | 4;
export const SETUP_STEPS: SetupStep[] = [1, 2, 3, 4];

export const SETUP_STEP_LABEL: Record<SetupStep, string> = {
  1: "Name",
  2: "Placements",
  3: "Images",
  4: "Go live",
};

export const SETUP_STEP_HEADING: Record<SetupStep, string> = {
  1: "Name your campaign",
  2: "Where should ads appear?",
  3: "Set up each placement",
  4: "Review and go live",
};

export const ALL_HOLES = Array.from({ length: 18 }, (_, i) => i + 1);

export type HoleMode = "ALL" | "FRONT" | "BACK" | "CHOOSE";

export const FRONT_NINE = ALL_HOLES.slice(0, 9);
export const BACK_NINE = ALL_HOLES.slice(9);

export interface PlacementSetup {
  /** Which segment is chosen; "CHOOSE" opens the 18-hole grid. */
  holeMode: HoleMode;
  /** Holes 1–18 this placement shows on; ignored when the placement doesn't use holes. */
  holes: number[];
  imageMode: "same" | "sponsors";
  /** "Same images on every hole", in show order. */
  images: SluggedId[];
  /** Hole sponsors: the sponsor image chips, in the order they were added. */
  sponsorImages: SluggedId[];
  /** Hole sponsors: which sponsor image owns each hole. */
  sponsorByHole: Record<number, SluggedId>;
}

export type DraftSource =
  | { kind: "blank" }
  /** campaignId is null while "Copy an existing campaign" is chosen but no campaign is picked yet. */
  | { kind: "copy"; campaignId: SluggedId | null; campaignName: string }
  /** CLUB-2657: steps 2 and 3 on an existing campaign; saving changes it in place. */
  | { kind: "edit"; campaignId: SluggedId };

export interface HoleLoadTiming {
  fadeIn: number;
  display: number;
  fadeOut: number;
}

export interface CampaignDraft {
  source: DraftSource;
  name: string;
  description: string;
  /** Selected placements, in catalog order. */
  placements: AdKitKey[];
  setup: Partial<Record<AdKitKey, PlacementSetup>>;
  /** Hole Load fade timing; undefined means the placement's metadata defaults. */
  timing?: HoleLoadTiming;
  /** Step 4: where Go live sends it. Empty is fine for Save draft. */
  simulatorIds: string[];
}

export const NAME_MAX_LENGTH = 100;

export function emptyDraft(): CampaignDraft {
  return {
    source: { kind: "blank" },
    name: "",
    description: "",
    placements: [],
    setup: {},
    simulatorIds: [],
  };
}

export function emptyPlacementSetup(): PlacementSetup {
  return {
    holeMode: "ALL",
    holes: [...ALL_HOLES],
    imageMode: "same",
    images: [],
    sponsorImages: [],
    sponsorByHole: {},
  };
}

export function isDraftDirty(draft: CampaignDraft): boolean {
  return (
    draft.source.kind !== "blank" ||
    draft.name.trim() !== "" ||
    draft.description.trim() !== "" ||
    draft.placements.length > 0
  );
}

export function isNameValid(name: string): boolean {
  const trimmed = name.trim();
  return trimmed.length > 0 && trimmed.length <= NAME_MAX_LENGTH;
}

type ReadinessPlacement = Pick<PlacementCatalogEntry, "isHoleEnabled">;

/** At least one image (or sponsored hole), and at least one hole if it uses holes. */
export function isPlacementReady(
  setup: PlacementSetup | undefined,
  placement: ReadinessPlacement,
): boolean {
  if (!setup) return false;
  if (placement.isHoleEnabled && setup.holes.length === 0) return false;
  if (setup.imageMode === "sponsors") {
    return setup.holes.some((hole) => setup.sponsorByHole[hole] !== undefined);
  }
  return setup.images.length > 0;
}

export function unreadyPlacements(
  draft: CampaignDraft,
  catalog: PlacementCatalogEntry[],
): PlacementCatalogEntry[] {
  const byKey = new Map(catalog.map((p) => [p.key, p]));
  return draft.placements.flatMap((key) => {
    const placement = byKey.get(key);
    if (!placement) return [];
    return isPlacementReady(draft.setup[key], placement) ? [] : [placement];
  });
}

/** Whether a step's own requirements are met. */
export function isStepComplete(
  step: SetupStep,
  draft: CampaignDraft,
  catalog: PlacementCatalogEntry[],
): boolean {
  switch (step) {
    case 1:
      return (
        isNameValid(draft.name) &&
        (draft.source.kind !== "copy" || draft.source.campaignId !== null)
      );
    case 2:
      return draft.placements.length > 0;
    case 3:
      return (
        draft.placements.length > 0 &&
        unreadyPlacements(draft, catalog).length === 0
      );
    case 4:
      return true;
  }
}

/** The furthest step the operator can open: every step before it must be complete. */
export function furthestReachableStep(
  draft: CampaignDraft,
  catalog: PlacementCatalogEntry[],
): SetupStep {
  for (const step of SETUP_STEPS) {
    if (step === 4 || !isStepComplete(step, draft, catalog)) return step;
  }
  return 4;
}

/**
 * A campaign's placements, holes, image order and hole sponsors as a new
 * draft. Images are referenced, not duplicated — the copy shares the library.
 */
export function draftFromCampaign(
  campaign: Pick<
    ClubhouseAdKitDto,
    "id" | "name" | "description" | "adLocations" | "transition"
  >,
  catalog: PlacementCatalogEntry[],
): CampaignDraft {
  const order = new Map(catalog.map((p, i) => [p.key, i]));
  const setup: CampaignDraft["setup"] = {};

  for (const location of campaign.adLocations ?? []) {
    if (location.deleted || !order.has(location.adKitMetaDataId)) continue;
    const enabled = location.enabledHoles as
      | Record<number, boolean>
      | undefined;
    const holes = enabled
      ? ALL_HOLES.filter((hole) => enabled[hole])
      : [...ALL_HOLES];
    const instances = (location.adInstances ?? [])
      .filter((i) => !i.deleted)
      .sort((a, b) => (a.orderLocation ?? 0) - (b.orderLocation ?? 0));
    const sponsored = instances.filter((i) => i.holeNumber > 0);
    const unique = (ids: SluggedId[]) => [...new Set(ids)];

    setup[location.adKitMetaDataId] = {
      holeMode: holeModeFor(holes),
      holes,
      imageMode: sponsored.length > 0 ? "sponsors" : "same",
      images: unique(
        instances.filter((i) => i.holeNumber === 0).map((i) => i.imageId),
      ),
      sponsorImages: unique(sponsored.map((i) => i.imageId)),
      sponsorByHole: Object.fromEntries(
        sponsored.map((i) => [i.holeNumber, i.imageId]),
      ),
    };
  }

  const placements = (Object.keys(setup) as unknown as AdKitKey[])
    .map(Number)
    .sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0)) as AdKitKey[];

  return {
    source: {
      kind: "copy",
      campaignId: campaign.id,
      campaignName: campaign.name,
    },
    name: `Copy of ${campaign.name}`.slice(0, NAME_MAX_LENGTH),
    description: campaign.description ?? "",
    placements,
    setup,
    timing: campaign.transition,
    // A copy starts as a draft, live nowhere until the operator chooses.
    simulatorIds: [],
  };
}

export function placementHasImages(setup: PlacementSetup | undefined): boolean {
  return !!setup && (setup.images.length > 0 || setup.sponsorImages.length > 0);
}

/** Adds or removes a placement, keeping the selection in catalog order. */
export function togglePlacement(
  draft: CampaignDraft,
  key: AdKitKey,
  catalog: PlacementCatalogEntry[],
): CampaignDraft {
  if (draft.placements.includes(key)) {
    const setup = { ...draft.setup };
    delete setup[key];
    return {
      ...draft,
      placements: draft.placements.filter((k) => k !== key),
      setup,
    };
  }
  const order = new Map(catalog.map((p, i) => [p.key, i]));
  return {
    ...draft,
    placements: [...draft.placements, key].sort(
      (a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0),
    ),
    setup: { ...draft.setup, [key]: emptyPlacementSetup() },
  };
}

/** The progress-strip status for one placement: "6 sponsored holes", "1 image", "Needs an image". */
export function placementStatus(
  setup: PlacementSetup | undefined,
  placement: ReadinessPlacement,
): string {
  if (!setup) return "Needs an image";
  if (placement.isHoleEnabled && setup.holes.length === 0) {
    return "Choose its holes";
  }
  if (setup.imageMode === "sponsors") {
    const count = setup.holes.filter(
      (hole) => setup.sponsorByHole[hole] !== undefined,
    ).length;
    return count === 0
      ? "Needs a hole sponsor"
      : `${count} sponsored ${count === 1 ? "hole" : "holes"}`;
  }
  const count = setup.images.length;
  return count === 0
    ? "Needs an image"
    : `${count} ${count === 1 ? "image" : "images"}`;
}

function sameHoles(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((hole, i) => hole === b[i]);
}

function holeModeFor(holes: number[]): HoleMode {
  if (sameHoles(holes, ALL_HOLES)) return "ALL";
  if (sameHoles(holes, FRONT_NINE)) return "FRONT";
  if (sameHoles(holes, BACK_NINE)) return "BACK";
  return "CHOOSE";
}

/** Picking a segment sets its holes; "Choose holes" keeps the current set to edit. */
export function setHoleMode(
  setup: PlacementSetup,
  mode: HoleMode,
): PlacementSetup {
  const holes =
    mode === "ALL"
      ? [...ALL_HOLES]
      : mode === "FRONT"
        ? [...FRONT_NINE]
        : mode === "BACK"
          ? [...BACK_NINE]
          : setup.holes;
  return { ...setup, holeMode: mode, holes };
}

export function toggleHole(
  setup: PlacementSetup,
  hole: number,
): PlacementSetup {
  const holes = setup.holes.includes(hole)
    ? setup.holes.filter((h) => h !== hole)
    : [...setup.holes, hole].sort((a, b) => a - b);
  return { ...setup, holes };
}

/** "all 18 holes", "the front 9", "6 holes" */
export function describeHoleChoice(setup: PlacementSetup): string {
  if (setup.holeMode === "ALL") return "all 18 holes";
  if (setup.holeMode === "FRONT") return "the front 9";
  if (setup.holeMode === "BACK") return "the back 9";
  return setup.holes.length === 1 ? "1 hole" : `${setup.holes.length} holes`;
}

/** location.enabledHoles: exactly 18 entries, true or false. */
export function enabledHolesMap(
  holes: number[],
): NonNullable<CreateAdKitLocationDto["enabledHoles"]> {
  return Object.fromEntries(
    ALL_HOLES.map((hole) => [hole, holes.includes(hole)]),
  ) as NonNullable<CreateAdKitLocationDto["enabledHoles"]>;
}

/** Adds to the end of the "same images" list; an image already there isn't added twice. */
export function addImage(setup: PlacementSetup, id: SluggedId): PlacementSetup {
  if (setup.images.includes(id)) return setup;
  return { ...setup, images: [...setup.images, id] };
}

export function moveImage(
  setup: PlacementSetup,
  index: number,
  direction: -1 | 1,
): PlacementSetup {
  const target = index + direction;
  if (target < 0 || target >= setup.images.length) return setup;
  const images = [...setup.images];
  [images[index], images[target]] = [images[target], images[index]] as [
    SluggedId,
    SluggedId,
  ];
  return { ...setup, images };
}

export function removeImage(
  setup: PlacementSetup,
  index: number,
): PlacementSetup {
  return { ...setup, images: setup.images.filter((_, i) => i !== index) };
}

/** Whether switching image modes would throw away what's set up in the current one. */
export function imageModeSwitchDropsData(setup: PlacementSetup): boolean {
  return setup.imageMode === "same"
    ? setup.images.length > 0
    : setup.sponsorImages.length > 0;
}

/** Switches modes, clearing the one being left so a save never sends both. */
export function setImageMode(
  setup: PlacementSetup,
  mode: PlacementSetup["imageMode"],
): PlacementSetup {
  if (setup.imageMode === mode) return setup;
  return {
    ...setup,
    imageMode: mode,
    images: [],
    sponsorImages: [],
    sponsorByHole: {},
  };
}

export function addSponsorImage(
  setup: PlacementSetup,
  id: SluggedId,
): PlacementSetup {
  if (setup.sponsorImages.includes(id)) return setup;
  return { ...setup, sponsorImages: [...setup.sponsorImages, id] };
}

/** Gives a hole to the sponsor; clicking a hole the sponsor already owns clears it. */
export function toggleSponsorHole(
  setup: PlacementSetup,
  hole: number,
  sponsor: SluggedId,
): PlacementSetup {
  const sponsorByHole = { ...setup.sponsorByHole };
  if (sponsorByHole[hole] === sponsor) delete sponsorByHole[hole];
  else sponsorByHole[hole] = sponsor;
  return { ...setup, sponsorByHole };
}

export function giveEmptyHolesTo(
  setup: PlacementSetup,
  sponsor: SluggedId,
): PlacementSetup {
  const sponsorByHole = { ...setup.sponsorByHole };
  for (const hole of setup.holes) {
    if (sponsorByHole[hole] === undefined) sponsorByHole[hole] = sponsor;
  }
  return { ...setup, sponsorByHole };
}

export function sponsoredHoleCount(
  setup: PlacementSetup,
  sponsor?: SluggedId,
): number {
  return setup.holes.filter((hole) =>
    sponsor === undefined
      ? setup.sponsorByHole[hole] !== undefined
      : setup.sponsorByHole[hole] === sponsor,
  ).length;
}

/** "All 18 holes · 3 images", "Front 9 · 6 sponsored holes", "2 images" */
export function describePlacementSetup(
  setup: PlacementSetup | undefined,
  placement: Pick<PlacementCatalogEntry, "isHoleEnabled">,
): string {
  if (!setup) return "";
  const parts: string[] = [];
  if (placement.isHoleEnabled) {
    const labels: Record<HoleMode, string> = {
      ALL: "All 18 holes",
      FRONT: "Front 9",
      BACK: "Back 9",
      CHOOSE: `${setup.holes.length} ${setup.holes.length === 1 ? "hole" : "holes"}`,
    };
    parts.push(labels[setup.holeMode]);
  }
  if (setup.imageMode === "sponsors") {
    const n = setup.holes.filter(
      (h) => setup.sponsorByHole[h] !== undefined,
    ).length;
    parts.push(`${n} sponsored ${n === 1 ? "hole" : "holes"}`);
  } else {
    const n = setup.images.length;
    parts.push(`${n} ${n === 1 ? "image" : "images"}`);
  }
  return parts.join(" · ");
}

/** A placement's images as the API stores them, in show order. */
export function placementInstances(
  setup: PlacementSetup,
): { imageId: SluggedId; holeNumber: number }[] {
  if (setup.imageMode === "sponsors") {
    return [...setup.holes]
      .sort((a, b) => a - b)
      .filter((hole) => setup.sponsorByHole[hole] !== undefined)
      .map((hole) => ({
        imageId: setup.sponsorByHole[hole] as SluggedId,
        holeNumber: hole,
      }));
  }
  return setup.images.map((imageId) => ({ imageId, holeNumber: 0 }));
}

export function locationPayload(
  key: AdKitKey,
  setup: PlacementSetup,
  placement: Pick<PlacementCatalogEntry, "isHoleEnabled">,
): CreateAdKitLocationDto {
  return {
    adKitMetaDataId: key,
    ...(placement.isHoleEnabled && {
      enabledHoles: enabledHolesMap(setup.holes),
    }),
    // Setup already checked each image fits the placement's shape; without
    // the override, a size warning makes the API skip the instance.
    overrideWarnings: true,
    adInstances: placementInstances(setup).map((instance) => ({
      ...instance,
      overrideWarnings: true,
      orderLocation: -1,
      widthAdjustment: -1,
      heightAdjustment: -1,
    })),
  };
}

/**
 * The one POST Save draft and Go live both send (CLUB-2655). orderLocation -1
 * lets the API number instances by list position, so list order is show order.
 */
export function buildCreatePayload(
  draft: CampaignDraft,
  catalog: Pick<
    PlacementCatalogEntry,
    "key" | "isHoleEnabled" | "hasTransitions"
  >[],
): CreateAdKitDto {
  const byKey = new Map(catalog.map((p) => [p.key, p]));
  const adLocations: CreateAdKitLocationDto[] = [];
  let hasTransitions = false;

  for (const key of draft.placements) {
    const placement = byKey.get(key);
    const setup = draft.setup[key];
    if (!placement || !setup) continue;
    hasTransitions ||= placement.hasTransitions;
    adLocations.push(locationPayload(key, setup, placement));
  }

  const description = draft.description.trim();
  return {
    name: draft.name.trim(),
    ...(description && { description }),
    ...(hasTransitions &&
      draft.timing && {
        transitionFadeIn: draft.timing.fadeIn,
        transitionDisplay: draft.timing.display,
        transitionFadeOut: draft.timing.fadeOut,
      }),
    adLocations,
  };
}
